/**
 * Cross-component state for demo sessions that needs to survive a chat
 * switch (navigate to another session and back) or a page reload.
 *
 * Two concerns live here:
 *
 *   1. **Just-restored flag** — TextBlock streams its text char-by-char on
 *      mount. When a saved demo session is reloaded, every TextBlock would
 *      re-stream content the user has already seen. We mark a short grace
 *      window (~300 ms) immediately after loadDemoSession so TextBlocks
 *      that mount during it render fully and skip the animation.
 *
 *   2. **Persisted approval resolutions** — NeoApprovalCard's resolved
 *      status lives in component-local React state, so it resets to
 *      PENDING on remount. For demo proposals (`demo-…` ids) we persist
 *      the chosen action in localStorage and re-hydrate the card to its
 *      APPROVED / REJECTED end-state on mount.
 *
 *      Resolutions are scoped to the demo **session** (`${sessionId}:${proposalId}`)
 *      when a sessionId is supplied. A fresh demo chat gets a brand-new
 *      `demo-…` sessionId, so its cards start PENDING even if the same
 *      proposalId was approved in a prior run; reload / navigate-away-and-back
 *      keep the SAME sessionId, so the resolution still restores. Callers with
 *      no session context (e.g. insight cards) omit sessionId and fall back to
 *      the legacy global-by-proposalId key.
 *
 * Both pieces are demo-only — real backend approvals already round-trip
 * through `/result` polling, and the live chat doesn't go through
 * loadDemoSession.
 */

const RESTORED_GRACE_MS = 300

let restoredAt = 0

/**
 * Call right before setMessages() in loadDemoSession. Opens a brief window
 * during which TextBlocks render their content fully instead of streaming.
 */
export function markDemoSessionRestored(): void {
  restoredAt = Date.now()
}

/**
 * True for ~300 ms after markDemoSessionRestored(). Long enough for every
 * restored TextBlock to mount and check it, short enough that any later
 * TextBlock added by live demo replay still animates normally.
 */
export function isDemoSessionJustRestored(): boolean {
  return Date.now() - restoredAt < RESTORED_GRACE_MS
}

const APPROVAL_STORAGE_KEY = 'neo_demo_approval_resolutions'

type DemoApprovalAction = 'approve' | 'reject'

function readApprovalMap(): Record<string, DemoApprovalAction> {
  try {
    const raw = localStorage.getItem(APPROVAL_STORAGE_KEY)
    if (!raw) {
      return {}
    }
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {}
    }
    const result: Record<string, DemoApprovalAction> = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (value === 'approve' || value === 'reject') {
        result[key] = value
      }
    }
    return result
  } catch {
    return {}
  }
}

/**
 * Build the localStorage key for a resolution. Scoped to the demo session
 * when a sessionId is given so a fresh chat (new sessionId) starts PENDING;
 * falls back to the legacy global-by-proposalId key when it isn't.
 */
function approvalKey(proposalId: string, sessionId?: string): string {
  return sessionId ? `${sessionId}:${proposalId}` : proposalId
}

/**
 * Stash the user's choice on a demo approval so the card can re-hydrate
 * to its done-state after a chat switch or reload. Pass the demo `sessionId`
 * to scope the resolution to this session (see file header).
 */
export function persistDemoApprovalResolution(
  proposalId: string,
  action: DemoApprovalAction,
  sessionId?: string,
): void {
  try {
    const map = readApprovalMap()
    map[approvalKey(proposalId, sessionId)] = action
    localStorage.setItem(APPROVAL_STORAGE_KEY, JSON.stringify(map))
  } catch {
    // localStorage can fail in private browsing; the only consequence is
    // the card resetting to PENDING on the next load.
  }
}

/**
 * Look up a previously-persisted resolution. Returns null if the user
 * never resolved this proposal in this session (or the entry was cleared).
 */
export function readDemoApprovalResolution(
  proposalId: string,
  sessionId?: string,
): DemoApprovalAction | null {
  return readApprovalMap()[approvalKey(proposalId, sessionId)] ?? null
}

/**
 * Wipe every persisted resolution. Called when demo mode is disabled or
 * the user clears their demo session history, so resolutions don't
 * outlive the sessions that produced them.
 */
export function clearDemoApprovalResolutions(): void {
  try {
    localStorage.removeItem(APPROVAL_STORAGE_KEY)
  } catch {
    // ignore — same rationale as persistDemoApprovalResolution
  }
}

const INSIGHT_STATE_KEY = 'neo_demo_insight_states'

export type DemoInsightCardState = 'dismissed' | 'snoozed'

function readInsightStateMap(): Record<string, DemoInsightCardState> {
  try {
    const raw = localStorage.getItem(INSIGHT_STATE_KEY)
    if (!raw) {
      return {}
    }
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {}
    }
    const result: Record<string, DemoInsightCardState> = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (value === 'dismissed' || value === 'snoozed') {
        result[key] = value
      }
    }
    return result
  } catch {
    return {}
  }
}

export function persistDemoInsightState(
  insightId: string,
  state: DemoInsightCardState,
): void {
  try {
    const map = readInsightStateMap()
    map[insightId] = state
    localStorage.setItem(INSIGHT_STATE_KEY, JSON.stringify(map))
  } catch {
    // ignore — same rationale as persistDemoApprovalResolution
  }
}

export function readDemoInsightState(
  insightId: string,
): DemoInsightCardState | null {
  return readInsightStateMap()[insightId] ?? null
}

export function clearDemoInsightStates(): void {
  try {
    localStorage.removeItem(INSIGHT_STATE_KEY)
  } catch {
    // ignore
  }
}
